import { useState, useEffect, useMemo, useCallback } from 'react'; // Added useCallback
import { doc, DocumentData, onSnapshot, Unsubscribe } from 'firebase/firestore'; // Changed getDoc to onSnapshot, Unsubscribe
import { db } from '@/utils/firebase';
import { z, ZodSchema, ZodTypeDef } from 'zod'; // Import z and ZodTypeDef

interface UseMultipleDocumentDataResult<T> {
  data: T[];
  loading: boolean;
  error: Error | undefined;
}

export const useMultipleDocumentData = <T, InputType = any>( // Added InputType generic
  collectionPath: string,
  ids: string[] | undefined | null,
  schema: z.ZodType<T, ZodTypeDef, InputType> // Use z.ZodType for more specific schema typing
): UseMultipleDocumentDataResult<T> => {
  // Store data as a map for easier updates, then convert to array for return
  const [dataMap, setDataMap] = useState<Map<string, T>>(new Map());
  const [loading, setLoading] = useState<boolean>(true); // Start true as we will fetch immediately
  const [error, setError] = useState<Error | undefined>(undefined);

  const idsString = useMemo(() => JSON.stringify(ids?.filter(id => !!id).sort()), [ids]);

  const parseAndSetDocument = useCallback((id: string, docData: DocumentData | undefined) => {
    if (docData) {
      try {
        const dataWithId = { ...docData, id };
        const parsedData = schema.parse(dataWithId);
        setDataMap(prevMap => new Map(prevMap).set(id, parsedData));
      } catch (parseError: any) {
        console.error(`Failed to parse document ${id} from ${collectionPath}:`, parseError);
        setError(prevError => prevError || new Error(`Parsing error for doc ${id}: ${parseError.message}`));
        // Optionally remove from map if parsing fails on update
        setDataMap(prevMap => {
          const newMap = new Map(prevMap);
          newMap.delete(id);
          return newMap;
        });
      }
    } else {
      // Document doesn't exist or data is undefined, remove from map
      setDataMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.delete(id);
        return newMap;
      });
      console.warn(`Document ${id} not found or undefined in ${collectionPath}`);
    }
  }, [collectionPath, schema]);

  useEffect(() => {
    const currentIds = idsString ? JSON.parse(idsString) as string[] : [];

    if (currentIds.length === 0) {
      setDataMap(new Map());
      setLoading(false);
      setError(undefined);
      return () => {}; // Return empty cleanup
    }

    setLoading(true);
    setError(undefined);
    // Reset dataMap for the new set of IDs to remove stale data
    setDataMap(new Map());

    const unsubscribes: Unsubscribe[] = [];

    currentIds.forEach(id => {
      if (!id) return; // Skip null or undefined ids if any slipped through

      const docRef = doc(db, collectionPath, id);
      const unsubscribe = onSnapshot(docRef, 
        (docSnap) => {
          parseAndSetDocument(id, docSnap.data());
          // Consider loading true until all initial snapshots are received
          // For simplicity, setLoading(false) will be handled after all listeners are attached
        },
        (snapshotError) => {
          console.error(`Error listening to document ${id} from ${collectionPath}:`, snapshotError);
          setError(prevError => prevError || snapshotError);
          // Remove from map on error
           setDataMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.delete(id);
            return newMap;
          });
        }
      );
      unsubscribes.push(unsubscribe);
    });
    
    // Set loading to false once all listeners are attached.
    // A more sophisticated approach might wait for the first snapshot from all docs.
    setLoading(false); 

    // Cleanup function
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [collectionPath, idsString, schema, parseAndSetDocument]);

  const dataArray = useMemo(() => Array.from(dataMap.values()), [dataMap]);

  return { data: dataArray, loading, error };
};
